import React, { Fragment, useState, useCallback, useRef, useEffect } from 'react';
import { Row, Col, Nav, Badge, Button, Dropdown, Modal, Form, Spinner, ProgressBar } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom-v5-compat';
import {
	editProjectAggregate,
	deleteProjectAggregate,
	// getAggregateTrackRecords
} from '@lib/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loading } from '@components/atomic/atoms';
import {
	ChevronRightSolid,
	IcoPadlockClosed,
	IcoPadlockOpen,
	IcoEllipsis,
	IcoEditBox,
	Trash,
	ExclamationBrown,
	ExclamationRed,
	IcoFileBreak,
	ConfirmSolid,
	RejectSolid,
	ArrowRight,
} from '@icons';
import { formatToDecimals } from '@lib/helpers/formatting';
import StatusBar from '@components/StatusBar';
import { translate } from 'components/translation/translation';
import GenerateTrackRecord from '@components/generateTrackRecord';
import ExportTrackRecordModal from './ExportTrackRecordModal';
import HorizontalBarGraph from './HorizontalBarGraph';
import { getProjectAggregateStatistics, getSalOptions } from '@lib/api_ts/projectAggregates';
import CustomFilter from '@components/project/Sampling/CustomFilter/CustomFilter';
import { getProjectAggregateTrackRecords } from '@lib/api_ts/trackRecords';
import FiltersToDisplay from '@components/project/Sampling/CustomFilter/FiltersToDisplay';
import { getProjectAggregate } from '@lib/api_ts/aggregatesApi';
import { WithPermission } from '@lib/helpers/WithPermission';

const AggregateDetail = () => {
	const { aggregateId } = useParams();
	const [newName, setNewName] = useState('');
	// const [advancementPercentage, setAdvancementPercentage] = useState({ expenseMade: 0, expenseToBeMade: 0 });
	// const [controlPercentage, setControlPercentage] = useState({ eligible: 0, inProgress: 0, notEligible: 0 });
	// const [graphData, setGraphData] = useState([]);
	const [isProjectsLong, setIsProjectsLong] = useState(false);
	const [isOneLineProjects, setIsOneLineProjects] = useState(false);
	const [expandedProjects, setExpandedProjects] = useState(false);
	const [showModalEdit, setShowModalEdit] = useState(false);
	const [showModalDelete, setShowModalDelete] = useState(false);
	const [showModalTrackRecord, setShowModalTrackRecord] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [sbShow, setSbShow] = useState(<></>);
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [hiddenCount, setHiddenCount] = useState(0);
	const projectsAggrRef = useRef(null);

	const { data: detail, isPending: isPendingDetail } = useQuery({
		queryKey: ['projectsAggregate', aggregateId],
		queryFn: async () => {
			return getProjectAggregate(aggregateId);
		},
		enabled: !!aggregateId,
		refetchOnMount: true,
		refetchOnReconnect: true,
		initialData: {},
	});

	const { data: trackRecords } = useQuery({
		queryKey: ['aggregateTrackRecords', aggregateId],
		queryFn: () => {
			return getProjectAggregateTrackRecords(aggregateId);
		},
		enabled: !!aggregateId,
	});

	const { data: salOptionsFromApi } = useQuery({
		queryKey: ['aggregateSalOptions', aggregateId],
		queryFn: () => {
			return getSalOptions(aggregateId);
		},
	});

	const [queryParams, setQueryParams] = useState('');
	const cupOptions =
		detail?.projects?.map((project) => ({
			label: project.cup,
			value: project.cup,
		})) ?? [];

	const trackRecordOptions =
		trackRecords?.map((trackRecord) => ({
			label: trackRecord.name,
			value: `${trackRecord.id}`,
		})) ?? [];

	const salOptions =
		salOptionsFromApi?.map((sal) => ({
			label: sal,
			value: sal,
		})) ?? [];

	const filterFields = [
		{
			name: 'cup',
			label: 'CUP',
			queryParamName: 'cup',
			options: cupOptions,
			searchCheckboxAdvancedFilter: true,
			searchCheckboxPlaceholder: 'Cerca un CUP...',
			optionsType: 'dynamic',
		},
		{
			name: 'trackrecord_id',
			label: 'Track Record',
			queryParamName: 'trackrecord_id',
			options: trackRecordOptions,
			searchCheckboxAdvancedFilter: true,
			searchCheckboxPlaceholder: 'Cerca un Track Record...',
			optionsType: 'dynamic',
		},
		{
			name: 'sal',
			label: 'SAL',
			queryParamName: 'sal',
			options: salOptions,
			searchCheckboxAdvancedFilter: true,
			searchCheckboxPlaceholder: 'Cerca un Track Record...',
			optionsType: 'dynamic',
		},
	];
	const [activeFilters, setActiveFilters] = useState({});

	const { data: statistics } = useQuery({
		queryKey: ['projectsAggregateStatistics', aggregateId, queryParams],
		queryFn: () => {
			return getProjectAggregateStatistics(aggregateId, queryParams);
		},
	});

	const handleModalClose = (action) => {
		setSbShow(<></>);
		setNewName('');

		if (action === 'edit') {
			setShowModalEdit(false);
		} else if (action === 'delete') {
			setShowModalDelete(false);
		}
	};

	const handleStatusBar = useCallback((status, message, timeout) => {
		setSbShow(<StatusBar status={status} message={message} timeout={timeout} />);
	}, []);

	useEffect(() => {
		const ref = projectsAggrRef.current;
		if (!ref) return;

		const updateVisibility = () => {
			const lineHeight = parseFloat(getComputedStyle(ref).lineHeight);
			const height = ref.scrollHeight;
			const lines = Math.round(height / lineHeight);

			setIsOneLineProjects(lines <= 1);
			setIsProjectsLong(lines > 2);

			const children = Array.from(ref.children);
			let lastVisibleIndex = -1;

			children.forEach((child, idx) => {
				const rect = child.getBoundingClientRect();
				const parentRect = ref.getBoundingClientRect();
				if (rect.bottom <= parentRect.bottom) {
					lastVisibleIndex = idx;
				}
			});

			setHiddenCount(children.length - (lastVisibleIndex + 1));
		};

		updateVisibility();

		const observer = new window.ResizeObserver(updateVisibility);
		observer.observe(ref);

		return () => {
			observer.disconnect();
		};
	}, [detail?.projects, expandedProjects]);

	const handleEdit = () => {
		setShowModalEdit(true);
	};

	const handleDelete = () => {
		setShowModalDelete(true);
	};

	const handleSubmit = async (e, action) => {
		e?.preventDefault();
		setIsLoading(true);

		if (action === 'edit') {
			const resp1 = await editProjectAggregate(aggregateId, { name: newName });

			if (resp1.success) {
				handleStatusBar('success', translate('common.message.edited', 'Elemento modificato con successo'), true);
				setIsLoading(false);

				setTimeout(() => {
					queryClient.invalidateQueries({ queryKey: ['projectsAggregate', aggregateId] });
					handleModalClose('edit');
				}, 2500);
			} else {
				setIsLoading(false);
				handleStatusBar(
					'error',
					resp1.success === false
						? Object.values(resp1.data).join()
						: translate('common.error.save', 'Si è verificato un errore durante il salvataggio delle informazioni'),
					false,
				);
			}
		} else if (action === 'delete') {
			const resp2 = await deleteProjectAggregate(aggregateId);

			if (resp2.success) {
				handleStatusBar('success', translate('common.message.deleted', 'Elemento eliminato correttamente'), true);
				setIsLoading(false);

				setTimeout(() => {
					queryClient.invalidateQueries({ queryKey: ['projectsAggregate'] });
					handleModalClose('delete');
					navigate('/projects');
				}, 2500);
			} else {
				setIsLoading(false);
				handleStatusBar(
					'error',
					resp2.success === false
						? Object.values(resp2.data).join()
						: translate('common.error.save', 'Si è verificato un errore durante il salvataggio delle informazioni'),
					false,
				);
			}
		}
	};

	if (isPendingDetail) return <Loading />;

	const graphData = [
		{
			field: 'reportedAmount',
			label: 'Rendicontato',
			mainValue: statistics?.reportedAmount ?? 0,
			color: 'var(--np-chart-color--green)',
			tooltip: `Rendicontato - ${formatToDecimals(statistics?.reportedAmount)} €`,
		},
		{
			field: 'requestedAmount',
			label: 'Richiesto',
			mainValue: statistics?.requestedAmount ?? 0,
			color: 'var(--np-chart-color--purple)',

			tooltip: `Richiesto - ${formatToDecimals(statistics?.requestedAmount)} €`,
		},
		{
			field: 'totalApprovedAmount',
			label: 'Approvato',
			mainValue: statistics?.totalApprovedAmount ?? 0,
			color: 'var(--np-chart-color--dark-blue)',

			tooltip: `Approvato - ${formatToDecimals(statistics?.totalApprovedAmount)} €`,
		},
		{
			field: 'cashedIn',
			label: 'Incassato',
			mainValue: statistics?.cashedIn ?? 0,
			color: 'var(--np-chart-color--blue)',
			tooltip:
				`Incassato - ${formatToDecimals(statistics?.cashedIn)} €` +
				(statistics?.advanceCashedIn ? ` di cui <br/>Anticipo - ${formatToDecimals(statistics?.advanceCashedIn)} €` : ''),
		},
		{
			field: 'notApproved',
			label: 'Non approvato',
			mainValue: statistics?.requestedAmount - statistics?.totalApprovedAmount ?? 0,
			color: 'var(--np-chart-color--light-blue)',
			tooltip: `Non approvato - ${formatToDecimals(statistics?.requestedAmount - statistics?.totalApprovedAmount)} €`,
		},
	];

	const spent = statistics?.finalAmount;
	const notSpent = statistics?.totalAmount - statistics?.finalAmount;
	const totalSpent = statistics?.finalAmount + notSpent;
	const advancementPercentage = {
		expenseMade: !totalSpent ? 0 : (spent * 100) / totalSpent,
		expenseToBeMade: !totalSpent ? 0 : (notSpent * 100) / totalSpent,
	};
	const totalControl = statistics?.finalAmount;
	const eligible = statistics?.compliantAmount;
	const inProgress = statistics?.inProgressAmount;
	const notEligible = statistics?.notCompliantAmount;
	const controlPercentage = {
		eligible: !totalControl ? 0 : (eligible * 100) / totalControl,
		inProgress: !totalControl ? 0 : (inProgress * 100) / totalControl,
		notEligible: !totalControl ? 0 : (notEligible * 100) / totalControl,
	};

	return (
		<>
			{showModalTrackRecord && (
				<GenerateTrackRecord
					entityName={detail.name}
					entityId={aggregateId}
					cancelFn={() => setShowModalTrackRecord(false)}
					showModalTrackRecord={showModalTrackRecord}
					entityType="projectaggregates"
					infratelSettings
				/>
			)}
			<Row className="my-4">
				<Col className="custom-project-header">
					<div className="breadcrumbs-navbar">
						<Nav.Item>
							<ChevronRightSolid fill="#063D57" />
							<Nav.Link href={`/projects`}>Progetti</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<ChevronRightSolid fill="#063D57" />
							<Nav.Link disabled style={{ textDecoration: 'none' }}>
								Gruppo {detail.name}
							</Nav.Link>
						</Nav.Item>
					</div>

					<div className="d-flex align-items-center justify-content-between mt-4 dropdown-custom-aggregate-detail-wrapper">
						<h2 className="mb-3 f-700">Gruppo</h2>
						<Dropdown align="end">
							<Dropdown.Toggle as="button" variant="link" className="p-0 border-0 bg-transparent" style={{ boxShadow: 'none' }}>
								<IcoEllipsis style={{ width: 32, height: 32 }} />
							</Dropdown.Toggle>

							<Dropdown.Menu>
								<Dropdown.Item onClick={handleEdit} className="dropdown-edit">
									<span className="me-2">Rinomina gruppo</span>
									<IcoEditBox style={{ width: 20, height: 20, fill: 'var(--np-text-primary-color)' }} />
								</Dropdown.Item>

								<Dropdown.Divider />

								<Dropdown.Item onClick={handleDelete} className="dropdown-delete">
									<span className="me-2">Elimina gruppo</span>
									<Trash className="ico-path-fill-red" style={{ width: 20, height: 20 }} />
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</div>

					<div className="d-flex flex-column mb-4">
						<h1 className="mb-3 fw-bold">{detail.name}</h1>

						<div className="d-flex justify-content-between">
							<div className="d-inline-flex align-items-center">
								<span className="me-2 subtitle-header">Accesso:</span>
								<Badge
									pill
									bg="info"
									style={{ width: '90px', height: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
								>
									<span className="badge-text">
										{detail.isPublic ? (
											<>
												Pubblico <IcoPadlockOpen className="ico-path-fill-white ms-1" height={12} />
											</>
										) : (
											<>
												Privato <IcoPadlockClosed className="ico-path-fill-white ms-1" height={12} />
											</>
										)}
									</span>
								</Badge>
							</div>
							<WithPermission permission="view_bi_dashboard">
								<Button
									as={Link}
									to={`/business-intelligence/projectaggregates/${aggregateId}`}
									target="_blank"
									className="btn custom-ghost-button d-flex align-items-center gap-2"
									style={{
										textTransform: 'none',
										fontWeight: '500',
									}}
								>
									Dashboard di monitoraggio avanzato <ArrowRight />
								</Button>
							</WithPermission>
						</div>

						<span className="mt-3 me-2 d-block subtitle-header">
							Descrizione:&nbsp;{!detail.description && <span className="f-400 ps-2">-</span>}
						</span>

						<span className="lh-28" style={{ color: '#4A5968' }}>
							{detail.description}
						</span>

						<Row className="mt-4 px-2">
							<Col className="col-4 ps-0 pe-3">
								<div
									style={{
										// backgroundColor: '#DEE4F1',
										backgroundColor: 'var(--np-secondary-color--ultralight)',
										padding: '1.5rem',
										borderRadius: '8px',
										height: '100%',
									}}
								>
									<span className="subtitle-header">Costo totale</span>

									<div className="f-700 fs-32 mt-2rem">
										<p>{formatToDecimals(detail.projectsSumTotalAmount)} €</p>
									</div>
								</div>
							</Col>

							<Col className="col-8 pe-0">
								<Row
									className="h-100 lh-28 p-4"
									style={{
										// backgroundColor: '#DEE4F1',
										backgroundColor: 'var(--np-primary)',
										borderRadius: '8px',
										alignItems: isOneLineProjects ? 'center' : 'flex-start',
									}}
								>
									<Col className="col-auto subtitle-header me-3 p-0">Progetti inclusi:</Col>

									<Col className="p-0">
										{detail.projects && detail.projects.length > 0 ? (
											<>
												<Row>
													<Col
														ref={projectsAggrRef}
														className={`list-projects-aggr${expandedProjects ? ' expanded' : ''}`}
														style={{ paddingTop: isOneLineProjects ? '5px' : '0', marginTop: isOneLineProjects ? '0' : '-5px' }}
													>
														{detail.projects.map((project, index) => (
															<span key={project.id} className="mb-3">
																<span>
																	<Button
																		as={Link}
																		variant="link"
																		className="project-link align-middle p-0"
																		style={{
																			paddingBottom: '-3px',
																		}}
																		to={`/project/${project.id}/dashboard`}
																	>
																		{project.name}
																	</Button>
																</span>
																{index < detail.projects.length - 1 && <span className="mx-2 project-link align-middle">•</span>}
															</span>
														))}
													</Col>
												</Row>
												<Row className="mt-2 align-items-center">
													{isProjectsLong && (
														<>
															<Col
																className="col-auto pe-0 title-link new-text-as-link"
																style={{
																	cursor: 'pointer',
																	// color: '#1C2743',
																}}
																onClick={() => setExpandedProjects((prev) => !prev)}
															>
																{!expandedProjects && <span className="f-700 me-2">+{hiddenCount}</span>}
																{expandedProjects ? 'mostra meno' : 'mostra tutti'}
																<span className={`dark svg-arrow${expandedProjects ? ' rotated' : ''}`}></span>
															</Col>
														</>
													)}
												</Row>
											</>
										) : (
											<span>Nessun progetto incluso</span>
										)}
									</Col>
								</Row>
							</Col>
						</Row>

						<hr style={{ border: '1px solid #E3E7EB', margin: '3rem 0', opacity: 1 }} />

						<h2 className="mb-2 f-700 d-block">Insights e Grafici del gruppo</h2>

						<span className="lh-28" style={{ color: '#4A5968' }}>
							Visualizza dati sintetici e rappresentazioni grafiche per monitorare l'andamento complessivo del gruppo.
						</span>

						<div
							style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '3rem', paddingBottom: '2rem' }}
						>
							{/* <div /> */}

							<CustomFilter
								activeFilters={activeFilters}
								setActiveFilters={setActiveFilters}
								filterFields={filterFields}
								queryParams={queryParams}
								setQueryParams={setQueryParams}
								hasNewButton
							/>
							{detail.canUseTrackRecord && (
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24 }}>
									<Button
										className="btn custom-outline-button"
										onClick={() => setShowModalTrackRecord(true)}
										style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, height: '48px' }}
									>
										<IcoFileBreak style={{ width: 20, height: 20 }} />
										Configura template
									</Button>
									<ExportTrackRecordModal aggregateId={detail.id} trackRecords={trackRecords} />
								</div>
							)}
						</div>
						<FiltersToDisplay
							filterFields={filterFields}
							activeFilters={activeFilters}
							setActiveFilters={setActiveFilters}
							queryParams={queryParams}
							setQueryParams={setQueryParams}
						/>
					</div>

					{/* <CustomFilter
						activeFilters={activeFilters}
						setActiveFilters={setActiveFilters}
						filterFields={filterFields}
						queryParams={queryParams}
						setQueryParams={setQueryParams}
						hasNewButton
					/> */}

					{/* {isPendingStatistics ? (
						<div className="d-flex justify-content-center my-5">
							<Spinner animation="border" className="" />
						</div>
					) : (
						<> */}
					<div className="p-4 mb-4" style={{ backgroundColor: '#ffffff', borderRadius: '8px' }}>
						<span className="f-700 fs-24 lh-34">Avanzamento spesa</span>

						<div className="lh-34 fs-24 mt-3 mb-4">Totale: {formatToDecimals(statistics?.totalAmount)} €</div>

						<ProgressBar
							// className="new-green-bar p-0 mb-3"
							className="aggregate_detail-bar p-0 mb-3"
							now={advancementPercentage.expenseMade}
							// now={90}
							min={0}
							max={100}
						/>

						<Row>
							<Col className="col-auto">
								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-2">
										<i
											// className="icon-square no-border bg-green"
											style={{
												backgroundColor: 'var(--np-chart-color--blue)',
											}}
											className="icon-square no-border"
										/>
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">Spesa effettuata ({formatToDecimals(advancementPercentage.expenseMade, 0)}%)</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics?.finalAmount)}€</span>
									</Col>
								</Row>
							</Col>
							<Col className="col-auto">
								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-2">
										<i
											// className="icon-square no-border bg-light-grey"
											className="icon-square no-border"
											style={{
												backgroundColor: 'var(--np-secondary-color--light)',
											}}
										/>
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">Spesa da effettuare ({formatToDecimals(advancementPercentage.expenseToBeMade, 0)}%)</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics?.totalAmount - statistics?.finalAmount)}€</span>
									</Col>
								</Row>
							</Col>
						</Row>
					</div>

					<div className="p-4 mb-4" style={{ backgroundColor: '#ffffff', borderRadius: '8px' }}>
						<span className="f-700 fs-24 lh-34">Avanzamento controllo</span>

						<div className="lh-34 fs-24 mt-4 mb-4">Totale: {formatToDecimals(statistics?.finalAmount)} €</div>

						<ProgressBar className="aggregate_detail-bar">
							<ProgressBar
								style={{
									backgroundColor: 'var(--np-chart-color--blue)',
								}}
								now={controlPercentage.eligible ?? 0}
								// key={'green' + (controlPercentage.eligible ?? 0)} //per forzare il rerender quando cambiano i valori
								label={`${formatToDecimals(controlPercentage.eligible ?? 0, 0)}%`}
								visuallyHidden
							/>
							<ProgressBar
								style={{
									backgroundColor: 'var(--np-chart-color--medium-blue)',
								}}
								now={controlPercentage.inProgress ?? 0}
								// key={'grey' + (controlPercentage.inProgress ?? 0)} //per forzare il rerender quando cambiano i valori
								label={`${formatToDecimals(controlPercentage.inProgress ?? 0, 0)}%`}
								visuallyHidden
							/>
						</ProgressBar>

						<Row className="mt-4">
							<Col className="col-auto">
								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-2">
										<i
											// className="icon-square no-border bg-light-grey"
											className="icon-square no-border"
											style={{
												backgroundColor: 'var(--np-chart-color--blue)',
											}}
										/>
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">Eleggibile ({formatToDecimals(controlPercentage.eligible, 0)}%)</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics?.compliantAmount)}€</span>
									</Col>
								</Row>
							</Col>
							<Col className="col-auto">
								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-2">
										<i
											// className="icon-square no-border bg-grey"
											className="icon-square no-border"
											style={{
												backgroundColor: 'var(--np-chart-color--medium-blue)',
											}}
										/>
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">In lavorazione ({formatToDecimals(controlPercentage.inProgress, 0)}%)</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics?.inProgressAmount)}€</span>
									</Col>
								</Row>
							</Col>
							<Col className="col-auto">
								<Row className="mt-3 align-items-center">
									<Col className="col-auto d-flex align-items-center pt-1 pe-2">
										<i
											// className="icon-square no-border bg-light-grey"
											className="icon-square no-border"
											style={{
												backgroundColor: 'var(--np-secondary-color--light)',
											}}
										/>
									</Col>
									<Col className="ps-0 pt-1">
										<span className="f-700">Non eleggibile ({formatToDecimals(controlPercentage.notEligible, 0)}%)</span>
										<span className="f-400 ps-2">{formatToDecimals(statistics?.notCompliantAmount)}€</span>
									</Col>
								</Row>
							</Col>
						</Row>

						<hr style={{ border: '1px solid #E3E7EB', margin: '1.5rem 0', opacity: 1 }} />

						<div>
							<Row className="justify-content-start">
								<Col className="">
									<span className="f-700">
										{statistics?.checkCompleted + statistics?.checkCompletedWithErrors ?? 0}/{statistics?.totalInvoices ?? 0}
										&nbsp;catene documentali controllate
									</span>
								</Col>
								<Col className="col-auto text-end">
									<span className="f-700">
										{formatToDecimals(
											!statistics?.totalInvoices
												? 0
												: ((statistics?.checkCompletedWithErrors + statistics?.checkCompleted) / statistics?.totalInvoices) * 100,
											0,
										)}
										%
									</span>
								</Col>
							</Row>

							<Row className="mt-4">
								<Col>
									<ProgressBar>
										<ProgressBar
											variant="danger"
											className="new"
											now={statistics?.percentageCompletedWithErrors ?? 0}
											// key={'red' + (statistics?.percentageCompletedWithErrors ?? 0)} //per forzare il rerender quando cambiano i valori
											label={`${formatToDecimals(statistics?.percentageCompletedWithErrors ?? 0, 0)}%`}
											visuallyHidden
											striped
										/>
										<ProgressBar
											variant="success"
											className="new"
											now={statistics?.percentageCompleted ?? 0}
											// key={'green' + (statistics?.percentageCompleted ?? 0)} //per forzare il rerender quando cambiano i valori
											label={`${formatToDecimals(statistics?.percentageCompleted ?? 0, 0)}%`}
											visuallyHidden
										/>
									</ProgressBar>
								</Col>
							</Row>

							<Row className="mt-4">
								<Col>
									<Row className="mt-3 align-items-start">
										<Col className="col-auto d-flex align-items-center pt-1 pe-1">
											<i className="icon-square no-border bg-green"></i>
										</Col>
										<Col className="col-auto">
											<ConfirmSolid width={20} height={20} />
										</Col>
										<Col className="ps-0 pt-0">
											<span className="f-700">
												{statistics?.checkCompleted ?? 0} catene documentali conformi (
												{formatToDecimals(statistics?.percentageCompleted ?? 0, 0)}%)
											</span>
											<span className="f-400 ps-2">
												{formatToDecimals(statistics?.completedAmount ?? 0)}€ eleggibili su importo campionato
											</span>
										</Col>
									</Row>
								</Col>
								<Col>
									<Row className="mt-3 align-items-start">
										<Col className="col-auto d-flex align-items-center pt-1 pe-1">
											<i className="icon-square bg-red-stripes"></i>
										</Col>
										<Col className="col-auto">
											<RejectSolid width={20} height={20} />
										</Col>
										<Col className="ps-0 pt-0">
											<span className="f-700">
												{statistics?.checkCompletedWithErrors ?? 0} catene documentali non conformi (
												{formatToDecimals(statistics?.percentageCompletedWithErrors ?? 0, 0)}%)
											</span>
											<span className="f-400 ps-2">
												{formatToDecimals(statistics?.errorAmount ?? 0)}€ non eleggibili su importo complessivo
											</span>
										</Col>
									</Row>
								</Col>
							</Row>
						</div>
					</div>

					<div className="p-4 mb-4" style={{ backgroundColor: '#ffffff', borderRadius: '8px' }}>
						<span className="f-700 fs-24 lh-34">Stato rendicontazione</span>

						<div
							className="p-4 mt-4"
							style={{ backgroundColor: '#E3E7EB', borderRadius: '8px', overflow: 'visible', position: 'relative' }}
						>
							<Row className="flex-column justify-content-center align-items-center w-100 m-auto">
								<Col className="col-12 d-flex justify-content-center align-items-center">
									{graphData.length > 0 && <HorizontalBarGraph data={graphData} />}
								</Col>
							</Row>
						</div>
					</div>
					{/* </>
					)} */}
				</Col>
			</Row>

			<Modal show={showModalEdit} onHide={() => handleModalClose('edit')} centered>
				<Modal.Header className="justify-content-center">
					<Modal.Title className="mt-3 d-flex flex-column align-items-center justify-content-center">
						<ExclamationBrown
							// fill="#1C2743"
							fill="var(--np-primary-color)"
							width={40}
							height={40}
						/>
						<span className="mt-3 f-700 fs-24">Modifica nome gruppo</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<span className="d-flex justify-content-center lh-28" style={{ marginBottom: '35px' }}>
						Aggiorna il nome del gruppo nell'apposito campo.
					</span>

					<Form>
						<Row className="flex-column mb-4">
							<Form.Group as={Col} className="mb-4 fs-16">
								<Form.Label>Nome attuale del gruppo</Form.Label>
								<Form.Text className="d-block">{detail.name}</Form.Text>
							</Form.Group>

							<Form.Group as={Col} className="mb-4">
								<Form.Label className="fs-16">Nuovo nome del gruppo</Form.Label>
								<Form.Control
									type="text"
									placeholder="Inserisci il nuovo nome del gruppo..."
									maxLength={70}
									name="name"
									value={newName}
									onChange={(e) => setNewName(e.target.value)}
									className="d-block"
								/>
								<Form.Text className="d-block input-description-text">Massimo {newName.length}/70 caratteri alfanumerici</Form.Text>
							</Form.Group>
						</Row>
					</Form>

					<div className="d-flex flex-column mb-2">
						<Button
							disabled={isLoading}
							onClick={() => handleModalClose('edit')}
							className="btn new custom-outline-button"
							style={{
								height: '48px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							Annulla
						</Button>

						<span className="text-center my-4">Oppure</span>

						<Button
							className="btn custom-primary-button d-flex justify-content-center align-items-center gap-2 lh-28"
							style={{
								height: '48px',
								fontWeight: 500,
							}}
							onClick={(e) => handleSubmit(e, 'edit')}
							disabled={isLoading}
						>
							Conferma nuovo nome
							{isLoading && <Spinner animation="border" size="sm" className="ms-2" />}
						</Button>
					</div>

					{sbShow}
				</Modal.Body>
			</Modal>

			<Modal show={showModalDelete} onHide={() => handleModalClose('delete')} centered>
				<Modal.Header className="justify-content-center">
					<Modal.Title className="mt-3 d-flex flex-column align-items-center justify-content-center">
						<ExclamationRed width={40} height={40} />
						<span className="mt-3 f-700 fs-24">Eliminazione gruppo</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="d-flex flex-column align-items-center text-center">
						<span>
							L'eliminazione del gruppo {detail.name} è un'azione irreversibile. Una volta confermata, non sarà possibile recuperarlo.
						</span>

						<span className="my-4">Sei sicuro di voler procedere?</span>
					</div>

					<div className="d-flex flex-column mb-2">
						<Button
							disabled={isLoading}
							onClick={(e) => handleSubmit(e, 'delete')}
							className="btn new custom-outline-error-button"
							style={{
								height: '48px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							Sì, procedi
							{isLoading && <Spinner animation="border" size="sm" className="ms-2" />}
						</Button>

						<span className="text-center my-4">Oppure</span>

						<Button
							className="btn custom-primary-button d-flex justify-content-center align-items-center gap-2 lh-28"
							style={{
								height: '48px',
								fontWeight: 500,
							}}
							onClick={() => handleModalClose('delete')}
							disabled={isLoading}
						>
							Annulla
						</Button>
					</div>

					{sbShow}
				</Modal.Body>
			</Modal>
		</>
	);
};
export default AggregateDetail;
